import { service } from '@ember/service';
import Route from '@ember/routing/route';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type SessionContextService from 'tio-employee/services/session-context';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type TasGroupModel from 'tio-data/models/tas-group';
import type TASProgramTemplateModel from 'tio-data/models/tas-program-template';
import type TasParticipantModel from 'tio-data/models/tas-participant';
import type RouterService from '@ember/routing/router';
import type FeatureService from 'tio-common/services/features';
import type PartnerService from 'tio-employee/services/partner';
import type ApiService from 'tio-employee/services/api';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import type { OpenSearchResponse } from 'tio-data/models/search';
import {
  type RecentApplication,
  ALL_APPLICATION_STATES,
  ALL_PROGRAM_INSTANCE_STATES,
  buildType4Instances,
  groupTasApplicationDocs,
  normalizePaymentFields,
} from 'tio-common/utils/tuition-assistance/application-states';

export default class AuthenticatedTasGettingStartedRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare features: FeatureService;
  @service declare router: RouterService;
  @service declare partner: PartnerService;
  @service declare api: ApiService;

  async model({ tas_program_template_id }: { tas_program_template_id: string }) {
    if (this.reimaginedData.isDemoMode) {
      return this.buildDemoModel(tas_program_template_id);
    }

    // Load the TasParticipant fresh to ensure we alert any HELD participant state
    // Include tas-eligibilities here to avoid duplicate queries
    const tasParticipants = await this.store.query<TasParticipantModel>('tas-participant', {
      filter: {
        employee: this.sessionContext.currentEmployee.id,
      },
      include: 'employee.dependents,tas-eligibilities',
    });

    if (tasParticipants.length === 0) {
      console.warn('No TAS participant found for current employee');
      this.router.transitionTo('authenticated.tas.dashboard');
      return null;
    }

    const tasParticipant = tasParticipants[0];

    // Load eligibilities from the already-fetched participant
    await this.tuitionAssistance.loadEligibilitiesForCurrentEmployee(tasParticipant);

    const tasGroups = await this.store.query<TasGroupModel>('tas-group', {
      filter: {
        company: this.sessionContext.currentEmployee.company.id,
        flavor: 'TAS.GroupFlavor.MUTUALLY_EXCLUSIVE',
      },
      include: 'tas-groupings.tas-program-template',
    });

    const activeInstances = await this.store.query<TasProgramInstanceModel>(
      'tas-program-instance',
      {
        filter: { employee: this.sessionContext.currentEmployee.id, active: true },
        include: 'tas-program-template',
      }
    );

    const programTemplate = await this.store.findRecord<TASProgramTemplateModel>(
      'tas-program-template',
      tas_program_template_id,
      {
        include: 'conditions.public-assets',
      }
    );

    // Fetch recent activity (2 months) and all applications for this program template
    const recentActivity = await this.fetchRecentActivity(programTemplate, false);
    const allApplications = await this.fetchRecentActivity(programTemplate, true);

    return {
      activeInstances: activeInstances,
      programTemplate: programTemplate,
      tasParticipant: tasParticipant,
      tasGroups: tasGroups,
      recentActivity,
      allApplications,
    };
  }

  /**
   * Build a model from demo data, bypassing all real API calls.
   * Pushes minimal Ember Data records into the store so the template's
   * computed getters (programName, isActiveEligibility, etc.) work as expected.
   */
  private async buildDemoModel(templateId: string) {
    const tasData = await this.reimaginedData.fetchTasData();
    const demoProgram = tasData.programs[0];
    const programName = demoProgram?.name ?? 'Tuition Assistance Program';
    const typeClassification = demoProgram?.typeClassification ?? 'TAS.ProgramType.4.2';

    // Same code on both the demo template and eligibility so that
    // hasActiveEligibilityForProgramTemplate returns true.
    const DEMO_CODE = 'DEMO_PROGRAM';

    const programTemplate = this.store.push({
      data: {
        id: templateId,
        type: 'tas-program-template',
        attributes: {
          code: DEMO_CODE,
          state: 'TAS.ProgramTemplateState.LAUNCHED',
          fields: {
            PROGRAM_NAME: { values: [programName] },
            PROGRAM_DESCRIPTION: { values: [''] },
            TYPE: { values: [typeClassification] },
            DEPENDENT_PROGRAM: { values: [false] },
            MULTIPLE_INSTANCE_PROGRAM: { values: [false] },
            ELIGIBILITY_WAITING_PERIOD: { values: [0] },
          },
          faqs: [],
          customFields: [],
          approverInstructions: [],
          isTrial: false,
        },
        relationships: {
          conditions: { data: [] },
          tasEligibilities: { data: [] },
          tasFieldOptions: { data: [] },
          tasGroups: { data: [] },
          tasGroupings: { data: [] },
          tasProgramInstances: { data: [] },
          versions: { data: [] },
        },
      },
    }) as TASProgramTemplateModel;

    this.store.push({
      data: {
        id: 'demo-eligibility',
        type: 'tas-eligibility',
        attributes: {
          code: DEMO_CODE,
          state: 'TAS.EligibilityState.ACTIVE',
        },
      },
    });

    const tasParticipant = this.store.push({
      data: {
        id: 'demo-participant',
        type: 'tas-participant',
        attributes: {
          state: 'TAS.ParticipantState.ACTIVE',
          employmentStartDate: null,
        },
        relationships: {
          tasEligibilities: {
            data: [{ type: 'tas-eligibility', id: 'demo-eligibility' }],
          },
          tasGroupings: { data: [] },
        },
      },
    }) as TasParticipantModel;

    // Populate the tuitionAssistance service's eligibility cache from our pushed records
    // so hasActiveEligibilityForProgramTemplate returns true without any API call.
    await this.tuitionAssistance.loadEligibilitiesForCurrentEmployee(tasParticipant);

    return {
      activeInstances: [] as TasProgramInstanceModel[],
      programTemplate,
      tasParticipant,
      tasGroups: [] as TasGroupModel[],
      recentActivity: { flatApplications: [], programInstances: [] },
      allApplications: { flatApplications: [], programInstances: [] },
    };
  }

  /**
   * Fetch applications for the specific program template
   * @param programTemplate - The program template to fetch applications for
   * @param showAll - If true, fetches all applications; if false, only past 2 months
   * Returns different data structure based on MULTIPLE_INSTANCE_PROGRAM setting
   */
  async fetchRecentActivity(
    programTemplate: TASProgramTemplateModel,
    showAll = false
  ): Promise<{
    flatApplications: RecentApplication[];
    programInstances: Array<{
      instanceApplication: RecentApplication;
      childApplications: RecentApplication[];
    }>;
  }> {
    const currentEmployee = this.sessionContext.currentEmployee;
    const companyId = currentEmployee.company?.id;
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

    if (!companyId) {
      return { flatApplications: [], programInstances: [] };
    }

    try {
      // Query for applications for this specific program template
      const builder = new OpenSearchQueryBuilder()
        .mustTerm('company_id', companyId)
        .mustTerm('employee_id', currentEmployee.id)
        .mustTerm('tas_program_template_id_keyword', programTemplate.id)
        .addFilter({
          bool: {
            should: [
              { terms: { tas_application_state_keyword: ALL_APPLICATION_STATES } },
              { terms: { tas_program_instance_state_keyword: ALL_PROGRAM_INSTANCE_STATES } },
            ],
            minimum_should_match: 1,
          },
        });

      // Only add date filter if not showing all
      if (!showAll) {
        builder.addFilter({ range: { updated_at: { gte: twoMonthsAgo.toISOString() } } });
      }

      builder.setSort('updated_at:desc').setPagination(1, 200);

      const query = {
        'model-type': 'TasProgramInstance',
        searchBody: builder.build(),
      };

      const response = (await this.api.call('/api/v2/search', {
        method: 'POST',
        body: JSON.stringify(query),
      })) as OpenSearchResponse<RecentApplication>;
      const responseBuilder = new OpenSearchResponseBuilder<RecentApplication>(response);
      const rawDocs = responseBuilder.getDocuments();
      const allApps = normalizePaymentFields(rawDocs);

      const { flatApps, type4InstanceMap } = groupTasApplicationDocs(allApps);
      const programInstances = buildType4Instances(type4InstanceMap);

      return { flatApplications: flatApps, programInstances };
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
      return { flatApplications: [], programInstances: [] };
    }
  }
}
